import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { ArrowTopRightOnSquareIcon, PlusIcon, TrashIcon, ClockIcon } from '@heroicons/react/24/outline';
import { HistoryDrawer } from '../../../components/HistoryDrawer';
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Drawer } from '../../../components/ui/Drawer';
import DateTimePicker from '../../../components/DateTimePicker';
import TypeAhead, { TypeAheadOption } from '../../../components/TypeAhead';
import repairOrdersService from '../../../services/repairOrders.service';
import RepairOrderDocuments, { PendingFile } from './RepairOrderDocuments';
import ChassisMapPanel from './ChassisMapPanel';
import OpenPingsSection from './OpenPingsSection';
import {
  resolveRepairUnitNumber,
  isChassisRepairPart,
  repairPartChangePatch,
  pickRepairRegion,
  buildBilledToOptions,
} from './repairOrderUnit';
import TabBar from '../../../components/TabBar';
import NotesPanel, { Note } from '../../../components/common/NotesPanel';
import api from '../../../services/api';
import { isValidEmailList } from '../../../utils/email';
import { config } from '../../../config/config';
import { getGoogleMapsLoaderOptions } from '../../../config/googleMaps';
import { useToast } from '../../../contexts/ToastContext';
import {
  RepairOrderDetail,
  RepairOrderStatusColors,
  RepairItemTypes,
  RepairLocationType,
  BilledToTypes,
  RepairCategory,
  RepairIssue,
  TireSize,
  ChassisPool,
  DriverLookup,
  EmployeeLookup,
  TireLoc,
  RepairDispatch,
  VendorLookup,
  GWILocation,
} from '../../../types/repairOrders';
import { ActiveUser } from '../../../hooks/useRealtimeCollaboration';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';

interface RepairOrderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  orderRecno: number | null; // null for new order
  onSaved: () => void; // refresh list after save
  viewers?: ActiveUser[]; // other users currently viewing this repair order
}

interface FormData {
  orderstatus: string;
  reportedbytype: string;
  reportedbyname: string;
  reportedbycontactnum: string;
  driverrecno: number | null;
  pronumber: number | null;
  repairpart: string;
  chassisid: string;
  container: string;
  chassispool: string;
  withequipment: string;
  waiting: string;
  locationtype: string;
  locationdesc: string;
  address1: string;
  address2: string;
  city: string;
  county: string;
  state: string;
  zip: string;
  locationid: number | null;
  customerlocationname: string;
  longitude: number | null;
  latitude: number | null;
  region: string;
  // Billing (populated after the order is created)
  estimatedcost: number | null;
  invoicenumber: string;
  invoicetotal: number | null;
  // Bill-to attribution: which party this cost is charged back to.
  billedtotype: string;
  billedtorecno: number | null;
  billedtoname: string;
  // Flat repair detail fields (single repair per order)
  repairtype: string;
  repaircategoryrecno: number;
  repairissue: number;
  repairqty: number;
  repairtiresize: string;
  repaircomments: string;
}

const REPORTED_BY_TYPES = ['GWI Employee', 'GWI Driver', 'Other'] as const;

const normalizeReportedByType = (value: string | null | undefined): string => {
  if (!value) return 'GWI Employee';
  const match = REPORTED_BY_TYPES.find(
    (t) => t.toLowerCase() === value.toLowerCase()
  );
  return match || 'Other';
};

const formatPhoneDisplay = (value: string): string => {
  // Strip non-digit characters for formatting attempt
  const digits = value.replace(/\D/g, '');
  // Only format if it looks like a 10-digit US number
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  // Otherwise return as-is (extensions, notes, etc.)
  return value;
};

// Dispatch fields we compare for staleness on save. Order matters for
// readability of the toast message but doesn't affect detection.
const DISPATCH_COMPARE_FIELDS: (keyof RepairDispatch)[] = [
  'vendorrecno',
  'estimateddispatchdatetime',
  'repairdispatchdatetime',
  'repaireta',
  'vendoretadatetime',
  'dispatchedby',
  'repairemail',
  'drivercomments',
];

/**
 * Compare the dispatch we loaded with the one currently on the server.
 * Returns the list of field names that differ. A '__new__' marker means a
 * dispatch was created externally while the user had none — this is the
 * primary scenario we're guarding against (two simultaneous dispatchers
 * each creating a record).
 */
function detectDispatchConflict(
  original: RepairDispatch | null | undefined,
  fresh: RepairDispatch | null | undefined,
): string[] {
  const changed: string[] = [];

  // We loaded with no dispatch but the server now has one — conflict.
  if (!original?.recno && fresh?.recno) {
    changed.push('__new__');
    for (const field of DISPATCH_COMPARE_FIELDS) {
      const v = fresh[field];
      if (v !== undefined && v !== null && v !== '') changed.push(field as string);
    }
    return changed;
  }

  // Dispatch existed and we both still see it: compare each field.
  if (original && fresh && original.recno === fresh.recno) {
    for (const field of DISPATCH_COMPARE_FIELDS) {
      const a = original[field] ?? '';
      const b = fresh[field] ?? '';
      if (String(a) !== String(b)) changed.push(field as string);
    }
  }

  return changed;
}

const initialFormData: FormData = {
  orderstatus: 'Open',
  reportedbytype: 'GWI Employee',
  reportedbyname: '',
  reportedbycontactnum: '',
  driverrecno: null,
  pronumber: null,
  repairpart: 'Chassis',
  chassisid: '',
  container: '',
  chassispool: '',
  withequipment: 'N',
  waiting: 'N',
  locationtype: 'GWI Yard / Warehouse / Facility',
  locationdesc: '',
  address1: '',
  address2: '',
  city: '',
  county: '',
  state: '',
  zip: '',
  locationid: null,
  customerlocationname: '',
  longitude: null,
  latitude: null,
  region: '',
  estimatedcost: null,
  invoicenumber: '',
  invoicetotal: null,
  billedtotype: '',
  billedtorecno: null,
  billedtoname: '',
  repairtype: 'Mechanical',
  repaircategoryrecno: 0,
  repairissue: 0,
  repairqty: 1,
  repairtiresize: '',
  repaircomments: '',
};

export default function RepairOrderDrawer({
  isOpen,
  onClose,
  orderRecno,
  onSaved,
  viewers = [],
}: RepairOrderDrawerProps) {
  const { accounts } = useMsal();
  const currentUserName = accounts[0]?.name || '';
  const currentUsername = accounts[0]?.username || currentUserName;
  const { showToast } = useToast();
  const { isLoaded: mapsLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());

  const drawerContentRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const drawerOpenCount = useRef(0);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Find the closest dialog element for portaling TypeAhead dropdowns
  // and move Google Places .pac-container into the dialog so it's visible
  useEffect(() => {
    if (isOpen) drawerOpenCount.current += 1;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && drawerContentRef.current) {
      const dialog = drawerContentRef.current.closest('dialog');
      setPortalContainer(dialog || null);

      if (dialog) {
        // Observe for Google Places .pac-container being added to body and move it into the dialog
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (node instanceof HTMLElement && node.classList.contains('pac-container')) {
                dialog.appendChild(node);
              }
            }
          }
        });
        observer.observe(document.body, { childList: true });
        return () => observer.disconnect();
      }
    }
  }, [isOpen]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookingUpPro, setLookingUpPro] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [v2repairorderid, setV2repairorderid] = useState<number | null>(null);
  const repairRequestRecno = useRef<number | null>(null);
  const lastLookedUpPro = useRef<number | null>(null);
  const [tirelocs, setTirelocs] = useState<TireLoc[]>([]);

  // Dispatch form state
  const [dispatchForm, setDispatchForm] = useState<RepairDispatch>({});

  // Snapshot of the dispatch when the order was loaded — used on save to
  // detect that another user added or modified the dispatch in the meantime.
  const originalDispatchRef = useRef<RepairDispatch | null>(null);
  // Names of dispatch fields that changed externally; drives amber-border
  // highlight on the inputs so the user can see what to review.
  const [staleDispatchFields, setStaleDispatchFields] = useState<string[]>([]);
  // Aborts the in-flight save's freshCheck/refresh fetches when the drawer
  // closes or switches to a different repair order.
  const saveAbortRef = useRef<AbortController | null>(null);

  // Lookup data
  const [allCategories, setAllCategories] = useState<RepairCategory[]>([]);
  const [issues, setIssues] = useState<RepairIssue[]>([]);
  const [tireSizes, setTireSizes] = useState<TireSize[]>([]);
  const [chassisPools, setChassisPools] = useState<ChassisPool[]>([]);
  const [drivers, setDrivers] = useState<DriverLookup[]>([]);
  const [employees, setEmployees] = useState<EmployeeLookup[]>([]);
  const [vendors, setVendors] = useState<VendorLookup[]>([]);
  const [gwiLocations, setGwiLocations] = useState<GWILocation[]>([]);
  const [customerLocations, setCustomerLocations] = useState<GWILocation[]>([]);

  // Pending document uploads for create mode
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  // Notes tab state
  const [activeTab, setActiveTab] = useState('information');
  const [noteCount, setNoteCount] = useState(0);

  // Pending notes ref for create mode — NotesPanel writes to it, we flush on save
  const pendingNotesRef = useRef<Note[]>([]);

  // Quick-create vendor modal state
  const [vendorCreateOpen, setVendorCreateOpen] = useState(false);
  const [vendorCreateName, setVendorCreateName] = useState('');
  const [vendorCreateEmail, setVendorCreateEmail] = useState('');
  const [vendorCreateSaving, setVendorCreateSaving] = useState(false);
  const [vendorCreateError, setVendorCreateError] = useState<string | null>(null);
  const {
    panelRef: vendorCreatePanelRef,
    panelStyle: vendorCreatePanelStyle,
    dragHandleProps: vendorCreateDragHandleProps,
  } = useModalDrag(vendorCreateOpen);

  // Send vendor email state
  const [sendingVendorEmail, setSendingVendorEmail] = useState(false);
  const [vendorEmailStatus, setVendorEmailStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  const isEditMode = orderRecno !== null;
  const isReadOnly = isEditMode && ['completed', 'closed', 'cancelled'].includes(formData.orderstatus?.toLowerCase() || '');
  // Billing stays editable on Completed (invoices land after the work is done);
  // only Closed/Cancelled lock it down.
  const isBillingLocked = isEditMode && ['closed', 'cancelled'].includes(formData.orderstatus?.toLowerCase() || '');
  const hasVendor = !!dispatchForm.vendorrecno;

  // Build TypeAhead options for drivers
  const driverOptions = useMemo<TypeAheadOption[]>(() =>
    drivers.map((d) => {
      const toTitleCase = (s: string) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
      const name = toTitleCase(`${d.firstname} ${d.lastname}`.trim());
      const unitLabel = d.unitnumber ? `${d.unitnumber} - ` : '';
      const label = `${unitLabel}${name}`;
      const parts: string[] = [];
      if (d.phone) parts.push(formatPhoneDisplay(d.phone));
      if (!d.active) parts.push('⚠ Inactive');
      return {
        value: String(d.recno),
        label,
        description: parts.length > 0 ? parts.join(' · ') : undefined,
        group: d.active ? 'Active' : 'Inactive',
      };
    }),
    [drivers]
  );

  // Build TypeAhead options for employees
  const employeeOptions = useMemo<TypeAheadOption[]>(() =>
    employees.map((e) => {
      const name = (e.display_name || '').trim();
      return {
        value: name,
        label: name,
        description: e.email || undefined,
      };
    }),
    [employees]
  );

  // Once lookups load, normalize reportedbyname to match option casing
  useEffect(() => {
    if (!formData.reportedbyname) return;
    if (formData.reportedbytype === 'GWI Employee' && employeeOptions.length > 0) {
      const match = employeeOptions.find(
        (o) => o.value.toLowerCase() === formData.reportedbyname.toLowerCase()
      );
      if (match && match.value !== formData.reportedbyname) {
        setFormData((prev) => ({ ...prev, reportedbyname: match.value }));
      }
    }
  }, [employeeOptions, formData.reportedbytype, formData.reportedbyname]);

  // Options for the "Billed To" entity picker, switched by the selected type.
  // Drivers render as "unit — First Last" to match how they're identified
  // elsewhere on this form; locations render by name.
  //
  // If the saved entity is missing from its lookup (deactivated driver, retired
  // location), the stored billedtoname is appended as a synthetic option so the
  // select still shows what the order was billed to instead of going blank.
  const billedToOptions = useMemo(() => buildBilledToOptions(
    formData.billedtotype,
    { drivers, customerLocations, gwiLocations },
    formData.billedtorecno,
    formData.billedtoname,
  ), [
    formData.billedtotype,
    formData.billedtorecno,
    formData.billedtoname,
    drivers,
    customerLocations,
    gwiLocations,
  ]);

  // Load static lookups on mount
  useEffect(() => {
    const loadLookups = async () => {
      const results = await Promise.allSettled([
        repairOrdersService.getTireSizes(),
        repairOrdersService.getChassisPools(),
        repairOrdersService.getDrivers(),
        repairOrdersService.getEmployees(),
        repairOrdersService.getVendors(),
        repairOrdersService.getGWILocations(),
        repairOrdersService.getCustomerLocations(),
      ]);
      const val = (r: PromiseSettledResult<{ data: any[] }>) => r.status === 'fulfilled' ? r.value.data || [] : [];
      setTireSizes(val(results[0]));
      setChassisPools(val(results[1]));
      setDrivers(val(results[2]));
      setEmployees(val(results[3]));
      setVendors(val(results[4]));
      setGwiLocations(val(results[5]));
      setCustomerLocations(val(results[6]));
      // Log any failures without blocking the rest
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['tire-sizes', 'chassis-pools', 'drivers', 'employees', 'vendors', 'gwi-locations', 'customer-locations'];
          console.error(`Failed to load ${names[i]} lookup:`, r.reason);
        }
      });
    };
    loadLookups();
  }, []);

  // Load all categories for the current equipment type (no repairtype filter).
  // Skip during initial order load — the order-loading effect pre-loads them.
  useEffect(() => {
    if (loading) return;
    if (!formData.repairpart) {
      setAllCategories([]);
      return;
    }
    repairOrdersService
      .getCategories('', formData.repairpart)
      .then((res) => setAllCategories(res.data || []))
      .catch(() => setAllCategories([]));
  }, [formData.repairpart, loading]);

  // Derive available repair types from loaded categories
  const repairTypeOptions = useMemo(
    () => [...new Set(allCategories.map((c) => c.repairtype))].sort(),
    [allCategories]
  );

  // Filter categories by selected repair type
  const categories = useMemo(
    () => allCategories.filter((c) => c.repairtype === formData.repairtype),
    [allCategories, formData.repairtype]
  );

  // Cascade: load issues when category changes
  // Skip during initial order load — the order-loading effect pre-loads issues
  useEffect(() => {
    if (loading) return;
    if (!formData.repaircategoryrecno) {
      setIssues([]);
      return;
    }
    repairOrdersService
      .getIssues(formData.repaircategoryrecno)
      .then((res) => setIssues(res.data || []))
      .catch(() => setIssues([]));
  }, [formData.repaircategoryrecno, loading]);

  // Determine if the selected category is a tire category
  const selectedCategory = categories.find((c) => c.recno === formData.repaircategoryrecno);
  const isTireCategory = selectedCategory?.istire === 'Y';

  // Load order data when editing
  useEffect(() => {
    if (!isOpen) return;

    if (orderRecno !== null) {
      setLoading(true);
      setError(null);
      repairOrdersService
        .get(orderRecno)
        .then(async (response) => {
          const order = response.order;
          setNoteCount(response.noteCount || 0);
          const repairtype = order.requests[0]?.repairtype || 'Mechanical';
          const repairpart = order.repairpart || 'Chassis';
          const repaircategoryrecno = order.requests[0]?.repaircategoryrecno || 0;

          // Pre-load all categories (no repairtype filter) and issues so dropdowns are populated before render
          const [catRes, issueRes] = await Promise.all([
            repairOrdersService.getCategories('', repairpart).catch(() => ({ data: [] })),
            repaircategoryrecno
              ? repairOrdersService.getIssues(repaircategoryrecno).catch(() => ({ data: [] }))
              : Promise.resolve({ data: [] }),
          ]);
          setAllCategories(catRes.data || []);
          setIssues(issueRes.data || []);

          setV2repairorderid(order.v2repairorderid || null);
          setFormData({
            orderstatus: order.orderstatus || 'Open',
            reportedbytype: normalizeReportedByType(order.reportedbytype),
            reportedbyname: order.reportedbyname || '',
            reportedbycontactnum: order.reportedbycontactnum || '',
            driverrecno: order.driverrecno,
            pronumber: order.pronumber,
            repairpart: repairpart,
            chassisid: order.chassisid || '',
            container: order.container || '',
            chassispool: order.chassispool || '',
            withequipment: order.withequipment || 'N',
            waiting: order.waiting || 'N',
            locationtype: order.locationtype || 'GWI Yard / Warehouse / Facility',
            locationdesc: order.locationdesc || '',
            address1: order.address1 || '',
            address2: order.address2 || '',
            city: order.city || '',
            county: order.county || '',
            state: order.state || '',
            zip: order.zip || '',
            locationid: order.locationid,
            customerlocationname: order.customerlocationname || '',
            longitude: order.longitude,
            latitude: order.latitude,
            region: order.region || '',
            estimatedcost: order.estimatedcost ?? null,
            invoicenumber: order.invoicenumber || '',
            invoicetotal: order.invoicetotal ?? null,
            billedtotype: order.billedtotype || '',
            billedtorecno: order.billedtorecno || null,
            billedtoname: order.billedtoname || '',
            repairtype: repairtype,
            repaircategoryrecno: repaircategoryrecno,
            repairissue: order.requests[0]?.repairissue || 0,
            repairqty: order.requests[0]?.repairqty || 1,
            repairtiresize: order.requests[0]?.repairtiresize || '',
            repaircomments: order.requests[0]?.repaircomments || '',
          });
          repairRequestRecno.current = order.requests[0]?.recno || null;
          lastLookedUpPro.current = order.pronumber || null;
          setTirelocs(order.requests[0]?.tirelocs || []);
          const existingDispatch = order.requests[0]?.dispatch || {};
          setDispatchForm({
            ...existingDispatch,
            // Only auto-fill dispatchedby with current user when a dispatch date exists
            dispatchedby:
              existingDispatch.dispatchedby ||
              (existingDispatch.repairdispatchdatetime ? currentUserName : ''),
          });
          // Snapshot the dispatch as loaded so we can detect a foreign add/edit on save.
          originalDispatchRef.current = order.requests[0]?.dispatch || null;
          setStaleDispatchFields([]);
        })
        .catch((err) => {
          setError(err.response?.data?.detail || 'Failed to load order');
        })
        .finally(() => setLoading(false));
    } else {
      // Reset form for new order, defaulting reporter to current user
      repairRequestRecno.current = null;
      lastLookedUpPro.current = null;
      setTirelocs([]);
      setDispatchForm({});
      originalDispatchRef.current = null;
      setStaleDispatchFields([]);
      setV2repairorderid(null);
      setFormData({
        ...initialFormData,
        reportedbyname: currentUserName,
      });
      setError(null);
    }
  }, [isOpen, orderRecno, currentUserName]);

  // Reset tab and pending state when drawer opens/closes or order changes
  useEffect(() => {
    setActiveTab('information');
    setNoteCount(0);
    setPendingFiles([]);
    pendingNotesRef.current = [];
  }, [isOpen, orderRecno]);

  // Abort any in-flight save freshness check when the drawer switches orders
  // or unmounts so the late response never lands on the wrong order's state.
  useEffect(() => {
    return () => {
      saveAbortRef.current?.abort();
      saveAbortRef.current = null;
    };
  }, [orderRecno]);

  // Returns true and applies stale-state highlights if another user has added
  // or modified this repair order's dispatch since we loaded it. Returns false
  // when there's no conflict (or no dispatch fields will be saved at all).
  const checkDispatchConflict = useCallback(
    async (signal: AbortSignal): Promise<boolean> => {
      if (!isEditMode || !orderRecno) return false;
      // Only relevant when the user is actually about to save dispatch info.
      const willSaveDispatch = !!(
        dispatchForm.vendorrecno ||
        dispatchForm.repairdispatchdatetime ||
        dispatchForm.dispatchedby ||
        dispatchForm.drivercomments ||
        dispatchForm.estimateddispatchdatetime ||
        dispatchForm.repairemail
      );
      if (!willSaveDispatch) return false;

      try {
        const fresh = await repairOrdersService.get(orderRecno, signal);
        if (signal.aborted) return false;
        const freshDispatch = fresh.order.requests?.[0]?.dispatch || null;
        const changed = detectDispatchConflict(originalDispatchRef.current, freshDispatch);
        if (changed.length === 0) return false;

        // Pull fresh dispatch into the form so the user sees server values,
        // and re-anchor the snapshot so the next save compares against this.
        setDispatchForm(freshDispatch || {});
        setStaleDispatchFields(changed);
        originalDispatchRef.current = freshDispatch;

        const message = changed.includes('__new__')
          ? 'A repair dispatch was added by another user. Highlighted fields were loaded — review and save again.'
          : 'This repair dispatch was updated by another user. Highlighted fields were changed — review and save again.';
        showToast({ type: 'warning', message, duration: 8000 });
        return true;
      } catch (err: any) {
        // Swallow abort; for other failures, fall through to normal save (best-effort).
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return false;
        return false;
      }
    },
    [isEditMode, orderRecno, dispatchForm, showToast]
  );

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  const handleFieldChange = useCallback(
    (field: keyof FormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  // Resolve region from whichever input is currently usable, preferring the
  // equipment's home location:
  //   1. Equipment unit — backend lookup. A blank/null/empty result is a MISS
  //      and falls through to the driver below.
  //   2. Driver (when reporter is Driver and a driver is selected) — preloaded, no call
  //   3. Neither hits — clear
  // Accepts overrides so callers can pass freshly-chosen values without
  // waiting for state to settle.
  const resolveRegion = useCallback(
    async (overrides?: { reportedbytype?: string; driverrecno?: number | null; unit?: string }) => {
      const reportedbytype = overrides?.reportedbytype ?? formData.reportedbytype;
      const driverrecno = overrides?.driverrecno ?? formData.driverrecno;
      const unit =
        overrides?.unit ??
        resolveRepairUnitNumber(formData.repairpart, formData.chassisid, formData.container);

      // Candidate 1: the equipment's home location (preferred). A failed
      // lookup is treated as a miss so we can fall through to the driver.
      let equipmentRegion = '';
      if (unit) {
        try {
          const result = await repairOrdersService.getRegionByEquipment(unit);
          equipmentRegion = result.region || '';
        } catch {
          // Lookup failed — leave blank and fall through to the driver.
        }
      }

      // Candidate 2: the reporting GWI driver's region.
      let driverRegion = '';
      if (reportedbytype === 'GWI Driver' && driverrecno) {
        const driver = drivers.find((d) => d.recno === driverrecno);
        driverRegion = driver?.region || '';
      }

      // Equipment wins when non-empty, else the driver, else ''.
      setFormData((prev) => ({
        ...prev,
        region: pickRepairRegion(equipmentRegion, driverRegion),
      }));
    },
    [
      formData.reportedbytype,
      formData.driverrecno,
      formData.repairpart,
      formData.chassisid,
      formData.container,
      drivers,
    ]
  );

  // Changing the equipment type clears the chassis-only fields the drawer is
  // about to hide — see repairPartChangePatch for why hiding alone is not
  // enough and why `container` is kept.
  const handleRepairPartChange = useCallback(
    (newPart: string) => {
      const patch = repairPartChangePatch(formData.repairpart, newPart);
      setFormData((prev) => ({ ...prev, ...patch }));

      // The unit this order points at moves with the type, so re-resolve the
      // region against the new one rather than leaving the old unit's region.
      resolveRegion({
        unit: resolveRepairUnitNumber(
          newPart,
          patch.chassisid ?? formData.chassisid,
          formData.container
        ),
      });
    },
    [formData.repairpart, formData.chassisid, formData.container, resolveRegion]
  );

  const handleReportedByTypeChange = useCallback(
    (newType: string) => {
      setFormData((prev) => ({
        ...prev,
        reportedbytype: newType,
        reportedbyname: newType === 'GWI Employee' ? currentUserName : '',
        reportedbycontactnum: '',
        driverrecno: null,
      }));
      // Switching type clears the driver, so always re-resolve.
      resolveRegion({ reportedbytype: newType, driverrecno: null });
    },
    [currentUserName, resolveRegion]
  );

  const handleDriverSelect = useCallback(
    (driverRecnoStr: string) => {
      const driver = drivers.find((d) => String(d.recno) === driverRecnoStr);
      if (driver) {
        const toTitleCase = (s: string) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
        const name = `${driver.unitnumber} - ${toTitleCase(`${driver.firstname} ${driver.lastname}`.trim())}`;
        setFormData((prev) => ({
          ...prev,
          reportedbyname: name,
          driverrecno: driver.recno,
          reportedbycontactnum: driver.phone ? formatPhoneDisplay(driver.phone) : prev.reportedbycontactnum,
        }));
        resolveRegion({ reportedbytype: 'GWI Driver', driverrecno: driver.recno });
      }
    },
    [drivers, resolveRegion]
  );

  const handleEmployeeSelect = useCallback(
    (employeeName: string) => {
      setFormData((prev) => ({
        ...prev,
        reportedbyname: employeeName,
      }));
    },
    []
  );

  const onAutocompleteLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry || !place.address_components) return;

    let streetNumber = '';
    let route = '';
    let city = '';
    let county = '';
    let state = '';
    let zip = '';

    for (const component of place.address_components) {
      const types = component.types;
      if (types.includes('street_number')) streetNumber = component.long_name;
      else if (types.includes('route')) route = component.long_name;
      else if (types.includes('locality')) city = component.long_name;
      else if (types.includes('administrative_area_level_2')) county = component.long_name.replace(/ County$/i, '');
      else if (types.includes('administrative_area_level_1')) state = component.short_name;
      else if (types.includes('postal_code')) zip = component.long_name;
    }

    const streetAddress = streetNumber ? `${streetNumber} ${route}` : route;

    setFormData((prev) => ({
      ...prev,
      address1: streetAddress,
      city,
      county,
      state,
      zip,
      locationdesc: prev.locationdesc,
      latitude: place.geometry!.location?.lat() || null,
      longitude: place.geometry!.location?.lng() || null,
    }));
  }, []);

  const handleProBlur = useCallback(async () => {
    const pro = formData.pronumber;
    if (!pro || pro === 0) return;
    if (pro === lastLookedUpPro.current) return;

    setLookingUpPro(true);
    try {
      const result = await repairOrdersService.lookupPro(pro);
      lastLookedUpPro.current = pro;
      setFormData((prev) => ({
        ...prev,
        container: result.container,
        chassisid: result.chassisid,
        chassispool: result.chassispool,
      }));
    } catch {
      // Pro not found — leave fields as-is
    } finally {
      setLookingUpPro(false);
    }
  }, [formData.pronumber]);

  // Cascade resets: when repair type changes, reset category and issue
  const handleRepairTypeChange = useCallback(
    (newType: string) => {
      setFormData((prev) => ({
        ...prev,
        repairtype: newType,
        repaircategoryrecno: 0,
        repairissue: 0,
        repairtiresize: '',
      }));
      setTirelocs([]);
    },
    []
  );

  // Cascade reset: when category changes, reset issue
  const handleCategoryChange = useCallback(
    (newCategoryRecno: number) => {
      setFormData((prev) => ({
        ...prev,
        repaircategoryrecno: newCategoryRecno,
        repairissue: 0,
        repairtiresize: '',
      }));
      setTirelocs([]);
    },
    []
  );

  // Build TypeAhead options for vendors
  const vendorOptions = useMemo<TypeAheadOption[]>(() =>
    vendors.map((v) => ({ value: String(v.recno), label: v.name })),
    [vendors]
  );

  // Build TypeAhead options for GWI locations
  const gwiLocationOptions = useMemo<TypeAheadOption[]>(() =>
    gwiLocations.map((loc) => ({
      value: String(loc.recno),
      label: loc.name,
      description: [loc.city, loc.state].filter(Boolean).join(', '),
    })),
    [gwiLocations]
  );

  const handleGWILocationSelect = useCallback(
    (recnoStr: string) => {
      const loc = gwiLocations.find((l) => String(l.recno) === recnoStr);
      if (!loc) return;
      setFormData((prev) => ({
        ...prev,
        locationid: loc.recno,
        locationdesc: prev.locationdesc,
        address1: loc.address1 || '',
        address2: loc.address2 || '',
        city: loc.city || '',
        county: loc.county || '',
        state: loc.state || '',
        zip: loc.zip || '',
        ...(loc.latitude != null && loc.longitude != null
          ? { latitude: loc.latitude, longitude: loc.longitude }
          : {}),
      }));
    },
    [gwiLocations]
  );

  // Build TypeAhead options for customer locations
  const customerLocationOptions = useMemo<TypeAheadOption[]>(() =>
    customerLocations.map((loc) => ({
      value: String(loc.recno),
      label: loc.name,
      description: [loc.city, loc.state].filter(Boolean).join(', '),
    })),
    [customerLocations]
  );

  const handleCustomerLocationSelect = useCallback(
    (recnoStr: string) => {
      const loc = customerLocations.find((l) => String(l.recno) === recnoStr);
      if (!loc) return;
      setFormData((prev) => ({
        ...prev,
        locationid: loc.recno,
        customerlocationname: loc.name,
        address1: loc.address1 || '',
        address2: loc.address2 || '',
        city: loc.city || '',
        county: loc.county || '',
        state: loc.state || '',
        zip: loc.zip || '',
        ...(loc.latitude != null && loc.longitude != null
          ? { latitude: loc.latitude, longitude: loc.longitude }
          : {}),
      }));
    },
    [customerLocations]
  );

  // Tailwind classes added to dispatch inputs whose values were just pulled
  // from a foreign change. The amber border tells the user "review this".
  const STALE_INPUT_CLASS = 'border-amber-500 bg-amber-50 dark:bg-amber-950 dark:border-amber-500';
  const isStaleDispatch = (field: keyof RepairDispatch) =>
    staleDispatchFields.includes(field as string);

  const handleDispatchFieldChange = useCallback(
    (field: keyof RepairDispatch, value: any) => {
      // Clear the stale highlight for this field once the user edits it.
      setStaleDispatchFields((prev) => prev.filter((f) => f !== field));
      setDispatchForm((prev) => {
        const updated = { ...prev, [field]: value };
        // Auto-fill repairemail from vendor's email when vendor is selected
        if (field === 'vendorrecno' && value) {
          const vendor = vendors.find((v) => v.recno === Number(value));
          if (vendor?.vendoremail) {
            updated.repairemail = vendor.vendoremail;
          }
        }
        // Auto-fill dispatch datetime with current time when vendor first selected
        if (field === 'vendorrecno' && value && !updated.repairdispatchdatetime) {
          const now = new Date();
          const pad = (n: number) => String(n).padStart(2, '0');
          updated.repairdispatchdatetime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
        }
        // Auto-fill dispatchedby with current user when a dispatch date is newly set
        // (either directly, or via the vendor-selection auto-fill above) and dispatchedby is still blank
        if (updated.repairdispatchdatetime && !prev.repairdispatchdatetime && !prev.dispatchedby && currentUserName) {
          updated.dispatchedby = currentUserName;
        }
        // Sync order status with vendor selection
        if (field === 'vendorrecno') {
          setFormData((prev) => ({ ...prev, orderstatus: value ? 'Dispatched' : 'Open' }));
        }
        // Auto-compute vendoretadatetime when dispatchdatetime or eta minutes change
        if (field === 'repairdispatchdatetime' || field === 'repaireta') {
          const base = field === 'repairdispatchdatetime' ? value : updated.repairdispatchdatetime;
          const eta = field === 'repaireta' ? value : updated.repaireta;
          if (base && eta && !isNaN(parseInt(eta, 10))) {
            const baseDate = new Date(base);
            baseDate.setMinutes(baseDate.getMinutes() + parseInt(eta, 10));
            const pad = (n: number) => String(n).padStart(2, '0');
            updated.vendoretadatetime = `${baseDate.getFullYear()}-${pad(baseDate.getMonth() + 1)}-${pad(baseDate.getDate())}T${pad(baseDate.getHours())}:${pad(baseDate.getMinutes())}`;
          } else if (!eta) {
            updated.vendoretadatetime = null;
          }
        }
        return updated;
      });
    },
    [vendors, currentUserName]
  );

  const handleSendVendorEmail = useCallback(async () => {
    if (!orderRecno) return;
    setSendingVendorEmail(true);
    setVendorEmailStatus('idle');
    try {
      await repairOrdersService.sendVendorEmail(orderRecno, dispatchForm.repairemail || '');
      setVendorEmailStatus('sent');
      setTimeout(() => setVendorEmailStatus('idle'), 4000);
    } catch {
      setVendorEmailStatus('error');
      setTimeout(() => setVendorEmailStatus('idle'), 4000);
    } finally {
      setSendingVendorEmail(false);
    }
  }, [orderRecno, dispatchForm.repairemail]);

  const handleSave = useCallback(async () => {
    if (!formData.reportedbyname.trim()) {
      setError('Reported By Name is required');
      return;
    }
    if (formData.reportedbytype === 'GWI Driver' && !formData.driverrecno) {
      setError('Please select a driver');
      return;
    }
    const equipmentField = isChassisRepairPart(formData.repairpart) ? formData.chassisid : formData.container;
    if (!equipmentField?.trim()) {
      setError(`${isChassisRepairPart(formData.repairpart) ? 'Chassis ID' : 'Equipment #'} is required`);
      return;
    }
    if (!formData.repaircategoryrecno) {
      setError('Repair Category is required');
      return;
    }
    if (!formData.repairissue) {
      setError('Repair Issue is required');
      return;
    }
    if (dispatchForm.repairemail && !isValidEmailList(dispatchForm.repairemail, { semicolon: true })) {
      setError('Enter valid repair email addresses separated by commas or semicolons.');
      return;
    }
    if (dispatchForm.vendorrecno && dispatchForm.repairdispatchdatetime) {
      if (!dispatchForm.repaireta) {
        setError('ETA minutes are required when a vendor and dispatch time are set');
        return;
      }
      if (!dispatchForm.vendoretadatetime) {
        setError('Vendor ETA time is required when a vendor and dispatch time are set');
        return;
      }
    }

    setSaving(true);
    setError(null);
    saveAbortRef.current?.abort();
    const controller = new AbortController();
    saveAbortRef.current = controller;

    try {
      // Pre-save dispatch conflict check: aborts the save if another user
      // added/modified the dispatch since we loaded the order.
      const conflict = await checkDispatchConflict(controller.signal);
      if (controller.signal.aborted) return;
      if (conflict) {
        setSaving(false);
        return;
      }

      const payload = {
        orderstatus: formData.orderstatus,
        reportedbytype: formData.reportedbytype,
        reportedbyname: formData.reportedbyname,
        reportedbycontactnum: formData.reportedbycontactnum,
        driverrecno: formData.driverrecno || 0,
        pronumber: formData.pronumber || 0,
        repairpart: formData.repairpart,
        chassisid: formData.chassisid,
        container: formData.container,
        chassispool: formData.chassispool,
        withequipment: formData.withequipment,
        waiting: formData.waiting,
        locationtype: formData.locationtype,
        locationdesc: formData.locationdesc,
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        county: formData.county,
        state: formData.state,
        zip: formData.zip,
        locationid: formData.locationid || 0,
        customerlocationname: formData.customerlocationname,
        longitude: formData.longitude || 0,
        latitude: formData.latitude || 0,
        region: formData.region,
        estimatedcost: formData.estimatedcost ?? 0,
        invoicenumber: formData.invoicenumber,
        invoicetotal: formData.invoicetotal ?? 0,
        billedtotype: formData.billedtotype,
        billedtorecno: formData.billedtorecno ?? 0,
        billedtoname: formData.billedtoname,
        // Wrap flat repair fields into requests array for backend compat
        requests: [
          {
            ...(repairRequestRecno.current ? { recno: repairRequestRecno.current } : {}),
            repairtype: formData.repairtype,
            repairstatus: formData.orderstatus,
            repaircategoryrecno: formData.repaircategoryrecno || 0,
            repairissue: formData.repairissue,
            repairqty: tirelocs.length || formData.repairqty,
            repairtiresize: formData.repairtiresize,
            repaircomments: formData.repaircomments,
            tirelocs,
          },
        ],
      };

      if (isEditMode && orderRecno) {
        await repairOrdersService.update(orderRecno, payload);
        // Save dispatch info if any fields are set
        if (dispatchForm.vendorrecno || dispatchForm.repairdispatchdatetime || dispatchForm.dispatchedby || dispatchForm.drivercomments || dispatchForm.estimateddispatchdatetime || dispatchForm.repairemail) {
          const result = await repairOrdersService.upsertDispatch(orderRecno, dispatchForm);
          setDispatchForm((prev) => ({ ...prev, recno: result.recno }));
        }
      } else {
        const createResult = await repairOrdersService.create(payload);

        // Upload any pending documents
        if (pendingFiles.length > 0) {
          for (const pf of pendingFiles) {
            try {
              await repairOrdersService.uploadDocument(createResult.recno, pf.file, pf.documentType, pf.description, formData.pronumber);
            } catch (uploadErr) {
              console.error('Failed to upload queued document:', uploadErr);
            }
          }
          setPendingFiles([]);
        }

        // Flush any pending notes
        if (pendingNotesRef.current.length > 0) {
          for (const pn of pendingNotesRef.current) {
            try {
              await repairOrdersService.addNote(createResult.recno, pn.notes);
            } catch (noteErr) {
              console.error('Failed to save queued note:', noteErr);
            }
          }
          pendingNotesRef.current = [];
        }
      }

      onSaved();
      onClose();
    } catch (err: any) {
      // Drawer was closed or order changed mid-save — silently bail.
      if (controller.signal.aborted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.response?.data?.detail || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  }, [formData, tirelocs, isEditMode, orderRecno, dispatchForm, pendingFiles, onSaved, onClose, checkDispatchConflict]);

  const handleStatusChange = useCallback(
    async (newStatus: string) => {
      if (!isEditMode || !orderRecno) return;

      // Apply the new status and do a full save (order + dispatch) so any
      // pending edits are persisted alongside the status change.
      setFormData((prev) => ({ ...prev, orderstatus: newStatus }));

      setSaving(true);
      setError(null);
      saveAbortRef.current?.abort();
      const controller = new AbortController();
      saveAbortRef.current = controller;

      try {
        // Same dispatch conflict check as handleSave — covers status changes
        // (Cancel, Mark Closed, etc.) which also persist the dispatch.
        const conflict = await checkDispatchConflict(controller.signal);
        if (controller.signal.aborted) return;
        if (conflict) {
          setSaving(false);
          return;
        }

        const payload = {
          orderstatus: newStatus,
          reportedbytype: formData.reportedbytype,
          reportedbyname: formData.reportedbyname,
          reportedbycontactnum: formData.reportedbycontactnum,
          driverrecno: formData.driverrecno || 0,
          pronumber: formData.pronumber || 0,
          repairpart: formData.repairpart,
          chassisid: formData.chassisid,
          container: formData.container,
          chassispool: formData.chassispool,
          withequipment: formData.withequipment,
          waiting: formData.waiting,
          locationtype: formData.locationtype,
          locationdesc: formData.locationdesc,
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          county: formData.county,
          state: formData.state,
          zip: formData.zip,
          locationid: formData.locationid || 0,
          customerlocationname: formData.customerlocationname,
          longitude: formData.longitude || 0,
          latitude: formData.latitude || 0,
          region: formData.region,
          // Billing fields must be sent on status changes too — the backend
          // overwrites these columns with defaults when they're absent, which
          // wiped the user's estimated cost / invoice data on Complete.
          estimatedcost: formData.estimatedcost ?? 0,
          invoicenumber: formData.invoicenumber,
          invoicetotal: formData.invoicetotal ?? 0,
          billedtotype: formData.billedtotype,
          billedtorecno: formData.billedtorecno ?? 0,
          billedtoname: formData.billedtoname,
          requests: [
            {
              ...(repairRequestRecno.current ? { recno: repairRequestRecno.current } : {}),
              repairtype: formData.repairtype,
              repairstatus: newStatus,
              repaircategoryrecno: formData.repaircategoryrecno || 0,
              repairissue: formData.repairissue,
              repairqty: tirelocs.length || formData.repairqty,
              repairtiresize: formData.repairtiresize,
              repaircomments: formData.repaircomments,
              tirelocs,
            },
          ],
        };

        await repairOrdersService.update(orderRecno, payload);

        if (dispatchForm.vendorrecno || dispatchForm.repairdispatchdatetime || dispatchForm.dispatchedby || dispatchForm.drivercomments || dispatchForm.estimateddispatchdatetime || dispatchForm.repairemail) {
          const result = await repairOrdersService.upsertDispatch(orderRecno, dispatchForm);
          setDispatchForm((prev) => ({ ...prev, recno: result.recno }));
        }

        onSaved();
        onClose();
      } catch (err: any) {
        if (controller.signal.aborted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          return;
        }
        setError(err.response?.data?.detail || 'Failed to update status');
      } finally {
        setSaving(false);
      }
    },
    [isEditMode, orderRecno, formData, tirelocs, dispatchForm, onSaved, onClose, checkDispatchConflict]
  );

  const handleVendorCreateNew = useCallback(async () => {
    if (!vendorCreateName.trim()) {
      setVendorCreateError('Vendor name is required.');
      return;
    }
    if (vendorCreateEmail.trim() && !isValidEmailList(vendorCreateEmail, { semicolon: true })) {
      setVendorCreateError('Enter a valid vendor email address.');
      return;
    }
    setVendorCreateSaving(true);
    setVendorCreateError(null);
    try {
      const response = await api.post('/repair-vendors', { name: vendorCreateName.trim(), vendoremail: vendorCreateEmail.trim() });
      const newVendor: VendorLookup = { recno: response.data.data.recno, name: response.data.data.name, vendoremail: response.data.data.vendoremail || '' };
      setVendors((prev) => [...prev, newVendor].sort((a, b) => a.name.localeCompare(b.name)));
      handleDispatchFieldChange('vendorrecno', newVendor.recno);
      // Set repairemail after handleDispatchFieldChange — functional updaters chain,
      // so this sees the state produced by the previous call and merges on top.
      if (newVendor.vendoremail) {
        setDispatchForm((prev) => ({ ...prev, repairemail: newVendor.vendoremail }));
      }
      setVendorCreateOpen(false);
      setVendorCreateName('');
      setVendorCreateEmail('');
    } catch (err: any) {
      setVendorCreateError(err.response?.data?.detail || 'Failed to create vendor.');
    } finally {
      setVendorCreateSaving(false);
    }
  }, [vendorCreateName, vendorCreateEmail]);

  const getStatusBadge = (status: string) => {
    const normalized = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : '';
    const colorClass = RepairOrderStatusColors[normalized] || RepairOrderStatusColors.Draft;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}>
        {status}
      </span>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <span className="text-lg font-semibold">
            {isEditMode ? `RO #${orderRecno}` : 'New Repair Order'}
          </span>
          {isEditMode && v2repairorderid && (
            <span className="text-sm font-normal text-muted">
              v2: {v2repairorderid}
            </span>
          )}
          {isEditMode && getStatusBadge(formData.orderstatus)}
          {isEditMode && (
            <button
              onClick={() => setHistoryOpen(true)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              title="View Repair Order History"
            >
              <ClockIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      }
      maxWidth="xl"
      contentClassName="relative flex-1 py-6 overflow-y-auto"
      footer={loading ? undefined :
        <div className="space-y-2">
          {(() => {
            // Hide self (multiple tabs of self share the same user_id but
            // different sids) and dedupe other users so multi-tab viewers
            // only count once.
            const currentUserId = accounts[0]?.localAccountId || accounts[0]?.homeAccountId;
            const seen = new Set<string>();
            const otherViewers = viewers.filter((v) => {
              if (v.user_id === currentUserId) return false;
              if (seen.has(v.user_id)) return false;
              seen.add(v.user_id);
              return true;
            });
            if (otherViewers.length === 0) return null;
            return (
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="font-medium">Being Viewed By:</span>
                <span>{otherViewers.map((v) => v.user_name).join(', ')}</span>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-2">
            {/* Readonly: Reopen (any closed-out state) + Mark Repair Order Closed (completed only) */}
            {isEditMode && isReadOnly && (
              <button
                type="button"
                onClick={() => handleStatusChange('Open')}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 rounded-md disabled:opacity-50"
              >
                Reopen
              </button>
            )}
            {isEditMode && isReadOnly && formData.orderstatus?.toLowerCase() === 'completed' && (
              <button
                type="button"
                onClick={() => handleStatusChange('Closed')}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-label bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
              >
                Mark Repair Order Closed
              </button>
            )}
            {/* Active: Mark Pending + Defer (no vendor) or Complete (has vendor) */}
            {isEditMode && !isReadOnly && !hasVendor && (
              <>
                <button
                  type="button"
                  onClick={() => handleStatusChange('Pending')}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 border border-transparent rounded-md hover:bg-orange-600 disabled:opacity-50"
                >
                  Mark Pending
                </button>
                <button
                  type="button"
                  onClick={() => handleStatusChange('Deferred')}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-500 border border-transparent rounded-md hover:bg-amber-600 disabled:opacity-50"
                >
                  Defer Repair Order
                </button>
              </>
            )}
            {isEditMode && !isReadOnly && hasVendor && (
              <button
                type="button"
                onClick={() => handleStatusChange('Completed')}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 dark:text-green-400 dark:bg-green-900/20 dark:hover:bg-green-900/30 rounded-md disabled:opacity-50"
              >
                Complete Repair Order
              </button>
            )}
            {/* Save + Close drawer */}
            {isEditMode && !isReadOnly && (
              <button
                type="button"
                onClick={() => handleStatusChange('Cancelled')}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Cancel Repair Order
              </button>
            )}
            {/* Save shows for active orders and for Completed (to persist billing
                edits); only Closed/Cancelled hide it entirely. */}
            {!isBillingLocked && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Order'}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Tabs */}
      <TabBar
        tabs={[
          { id: 'information', label: 'Information', current: activeTab === 'information' },
          { id: 'notes', label: 'Notes', current: activeTab === 'notes', ...(noteCount > 0 ? { badge: noteCount } : {}) },
          { id: 'chassis-map', label: 'GPS Location', current: activeTab === 'chassis-map' },
        ]}
        onTabChange={handleTabChange}
        compact
        noBorder
        className="mb-4"
      />

      {/* Open maintenance requests — above the tab content, so it is the first
          thing maintenance sees on this order whichever tab they land on, and
          OUTSIDE the information tab's `pointer-events-none` read-only wrapper:
          a cancelled/closed order is precisely the case whose stale ping most
          needs dismissing, and inside that wrapper the button would be dead. */}
      {isEditMode && <OpenPingsSection orderRecno={orderRecno} isOpen={isOpen} />}

      {/* Notes tab */}
      {activeTab === 'notes' && (
        <div className="px-4 sm:px-6">
          <NotesPanel
            fetchNotes={orderRecno ? () => repairOrdersService.getNotes(orderRecno) : null}
            addNote={orderRecno ? async (text) => { await repairOrdersService.addNote(orderRecno, text); } : null}
            pendingNotesRef={pendingNotesRef}
            onNoteCountChange={setNoteCount}
            isReadOnly={false}
            currentUser={currentUsername}
          />
        </div>
      )}

      {/* GPS Location tab */}
      {activeTab === 'chassis-map' && (
        <div className="px-4 sm:px-6">
          <ChassisMapPanel
            chassisIdentifier={resolveRepairUnitNumber(
              formData.repairpart,
              formData.chassisid,
              formData.container
            )}
          />
        </div>
      )}

      {/* Information tab */}
      {activeTab === 'information' && loading ? (
        <div className="flex items-center justify-center py-12 px-4 sm:px-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="ml-3 text-gray-600 dark:text-gray-400">Loading order...</p>
        </div>
      ) : activeTab === 'information' ? (
        <>
        <div ref={drawerContentRef} className={`space-y-6 px-4 sm:px-6${isReadOnly ? ' pointer-events-none opacity-60 select-none' : ''}`}>
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Section A: Reporter Info */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-heading mb-3">
              Reporter Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Reported By Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.reportedbytype}
                  onChange={(e) => handleReportedByTypeChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                >
                  {REPORTED_BY_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                {formData.reportedbytype === 'GWI Employee' ? (
                  <TypeAhead
                    options={employeeOptions}
                    value={formData.reportedbyname}
                    onChange={handleEmployeeSelect}
                    placeholder="Search employees..."
                    showSearch
                    showDescriptions
                    portalContainer={portalContainer}
                  />
                ) : formData.reportedbytype === 'GWI Driver' ? (
                  <TypeAhead
                    options={driverOptions}
                    value={drivers.find((d) => d.recno === formData.driverrecno) ? String(formData.driverrecno) : ''}
                    onChange={handleDriverSelect}
                    placeholder="Search drivers..."
                    showSearch
                    showDescriptions
                    groupBy
                    portalContainer={portalContainer}
                  />
                ) : (
                  <input
                    type="text"
                    value={formData.reportedbyname}
                    onChange={(e) => handleFieldChange('reportedbyname', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter name..."
                  />
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Contact Number
                </label>
                <input
                  type="text"
                  value={formData.reportedbycontactnum}
                  onChange={(e) => handleFieldChange('reportedbycontactnum', e.target.value)}
                  onBlur={(e) => {
                    const formatted = formatPhoneDisplay(e.target.value);
                    if (formatted !== e.target.value) {
                      handleFieldChange('reportedbycontactnum', formatted);
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                  placeholder="Phone number..."
                />
              </div>
            </div>
          </div>

          {/* Section B: Equipment Info */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-heading mb-3">
              Equipment Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Pro #
                  {lookingUpPro && <span className="ml-2 text-blue-500 text-xs">Looking up...</span>}
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.pronumber || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      handleFieldChange('pronumber', val ? parseInt(val) : null);
                    }}
                    onBlur={handleProBlur}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter pro #..."
                  />
                  {formData.pronumber ? (
                    <a
                      href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${formData.pronumber}&searchtype=pronumber`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      title="Open in CMSV1"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Equipment Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.repairpart}
                  onChange={(e) => handleRepairPartChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                >
                  {RepairItemTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  {isChassisRepairPart(formData.repairpart) ? 'Chassis ID' : 'Equipment #'}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={isChassisRepairPart(formData.repairpart) ? formData.chassisid : formData.container}
                    onChange={(e) =>
                      handleFieldChange(
                        isChassisRepairPart(formData.repairpart) ? 'chassisid' : 'container',
                        e.target.value
                      )
                    }
                    onBlur={() => resolveRegion()}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      isChassisRepairPart(formData.repairpart) ? 'Enter chassis ID...' : 'Enter equipment #...'
                    }
                  />
                  {(() => {
                    const equipNum = isChassisRepairPart(formData.repairpart) ? formData.chassisid : formData.container;
                    const isGwi =
                      (isChassisRepairPart(formData.repairpart) && formData.chassispool === 'GWI') ||
                      (!isChassisRepairPart(formData.repairpart) && formData.repairpart !== 'Container' && formData.repairpart !== 'Truck');
                    return equipNum && isGwi ? (
                      <a
                        href={`/data-management/equipment-list?search=${encodeURIComponent(equipNum)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        title="Open in Equipment List"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </a>
                    ) : null;
                  })()}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Region
                </label>
                <input
                  type="text"
                  readOnly
                  value={formData.region}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-900 text-label cursor-default"
                />
              </div>
              {isChassisRepairPart(formData.repairpart) && (
                <>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-muted mb-1">
                      Chassis Pool
                    </label>
                    <select
                      value={formData.chassispool}
                      onChange={(e) => handleFieldChange('chassispool', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select pool...</option>
                      {chassisPools.map((pool) => (
                        <option key={pool.recno} value={pool.abbreviation}>
                          {pool.name} ({pool.abbreviation})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2 flex items-center justify-between">
                    <label className="text-xs font-medium text-muted">
                      Is Mounted?
                    </label>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.withequipment === 'Y'}
                      onClick={() => handleFieldChange('withequipment', formData.withequipment === 'Y' ? 'N' : 'Y')}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${formData.withequipment === 'Y' ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}`}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.withequipment === 'Y' ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                  {formData.withequipment === 'Y' && (
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-muted mb-1">
                        Container #
                      </label>
                      <input
                        type="text"
                        value={formData.container}
                        onChange={(e) => handleFieldChange('container', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter container #..."
                      />
                    </div>
                  )}
                </>
              )}
              <div className="col-span-2 flex items-center justify-between">
                <label className="text-xs font-medium text-muted">
                  Waiting for Parts/Service?
                </label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.waiting === 'Y'}
                  onClick={() => handleFieldChange('waiting', formData.waiting === 'Y' ? 'N' : 'Y')}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${formData.waiting === 'Y' ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.waiting === 'Y' ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Section C: Location */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-heading mb-3">
              Location
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Location Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.locationtype}
                  onChange={(e) => handleFieldChange('locationtype', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                >
                  {Object.values(RepairLocationType).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              {formData.locationtype === RepairLocationType.GWIFacility && (
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted mb-1">
                    GWI Facility
                  </label>
                  <TypeAhead
                    options={gwiLocationOptions}
                    value={formData.locationid ? String(formData.locationid) : ''}
                    onChange={handleGWILocationSelect}
                    placeholder="Search GWI locations..."
                    showDescriptions={true}
                    portalContainer={portalContainer}
                  />
                </div>
              )}
              {formData.locationtype === RepairLocationType.Customer && (
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted mb-1">
                    Customer Location
                  </label>
                  <TypeAhead
                    options={customerLocationOptions}
                    value={customerLocations.find((l) => l.name === formData.customerlocationname) ? String(customerLocations.find((l) => l.name === formData.customerlocationname)!.recno) : ''}
                    onChange={handleCustomerLocationSelect}
                    placeholder="Search customer locations..."
                    showDescriptions={true}
                    portalContainer={portalContainer}
                  />
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Location Description
                </label>
                <textarea
                  value={formData.locationdesc}
                  onChange={(e) => handleFieldChange('locationdesc', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the location..."
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Address Lookup
                </label>
                {mapsLoaded ? (
                  <Autocomplete
                    key={drawerOpenCount.current}
                    onLoad={onAutocompleteLoad}
                    onPlaceChanged={onPlaceChanged}
                    options={{ componentRestrictions: { country: 'us' } }}
                  >
                    <input
                      type="text"
                      defaultValue=""
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                      placeholder="Search for an address..."
                    />
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    value={formData.locationdesc}
                    onChange={(e) => handleFieldChange('locationdesc', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    placeholder="Loading maps..."
                    disabled
                  />
                )}
              </div>
              {formData.address1 && (
                <div className="col-span-2 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">Address 1</label>
                    <input
                      type="text"
                      value={formData.address1}
                      onChange={(e) => handleFieldChange('address1', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">Address 2</label>
                    <input
                      type="text"
                      value={formData.address2}
                      onChange={(e) => handleFieldChange('address2', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                      placeholder="Suite, unit, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-900 text-label"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">County</label>
                    <input
                      type="text"
                      value={formData.county}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-900 text-label"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">State</label>
                    <input
                      type="text"
                      value={formData.state}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-900 text-label"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">Zip</label>
                    <input
                      type="text"
                      value={formData.zip}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-100 dark:bg-gray-900 text-label"
                    />
                  </div>
                </div>
              )}
              {formData.latitude && formData.longitude && mapsLoaded && (
                <div className="col-span-2 rounded-md overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height: 200 }}>
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{ lat: formData.latitude, lng: formData.longitude }}
                    zoom={14}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                      mapTypeControl: false,
                      streetViewControl: false,
                    }}
                  >
                    <Marker position={{ lat: formData.latitude, lng: formData.longitude }} />
                  </GoogleMap>
                </div>
              )}
              <div className="col-span-2 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted mb-1">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude ?? ''}
                    onChange={(e) => handleFieldChange('latitude', e.target.value ? parseFloat(e.target.value) : null)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 40.7128"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted mb-1">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude ?? ''}
                    onChange={(e) => handleFieldChange('longitude', e.target.value ? parseFloat(e.target.value) : null)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. -74.0060"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section D: Repair Details */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-heading mb-3">
              Repair Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Repair Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.repairtype}
                  onChange={(e) => handleRepairTypeChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                >
                  {repairTypeOptions.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.repaircategoryrecno}
                  onChange={(e) => handleCategoryChange(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat.recno} value={cat.recno}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Issue <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.repairissue}
                  onChange={(e) => handleFieldChange('repairissue', parseInt(e.target.value) || 0)}
                  disabled={!formData.repaircategoryrecno}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value={0}>
                    {formData.repaircategoryrecno ? 'Select issue...' : 'Select a category first'}
                  </option>
                  {issues.map((issue) => (
                    <option key={issue.recno} value={issue.recno}>
                      {issue.issue}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tire-specific fields (shown when category has istire='Y') */}
              {isTireCategory && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">
                      Tire Size
                    </label>
                    <select
                      value={formData.repairtiresize}
                      onChange={(e) => handleFieldChange('repairtiresize', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select size...</option>
                      {tireSizes.map((size) => (
                        <option key={size.recno} value={size.recno}>
                          {size.size}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted mb-1">
                      Quantity
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={tirelocs.length}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 text-heading cursor-default"
                    />
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-muted">
                        Tire Locations
                      </label>
                      <button
                        type="button"
                        onClick={() => setTirelocs((prev) => [...prev, { side: 'Left', axle: 'Front', position: 'Outer' }])}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Add Tire
                      </button>
                    </div>
                    {tirelocs.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500 italic">No tire locations added.</p>
                    ) : (
                      <div className="space-y-2">
                        {tirelocs.map((tl, idx) => (
                          <div key={tl.recno ?? `new-${idx}`} className="flex items-center gap-2">
                            <select
                              value={tl.side}
                              onChange={(e) => setTirelocs((prev) => prev.map((t, i) => i === idx ? { ...t, side: e.target.value } : t))}
                              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading"
                            >
                              <option value="Left">Left</option>
                              <option value="Right">Right</option>
                            </select>
                            <select
                              value={tl.axle}
                              onChange={(e) => setTirelocs((prev) => prev.map((t, i) => i === idx ? { ...t, axle: e.target.value } : t))}
                              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading"
                            >
                              <option value="Front">Front</option>
                              <option value="Middle">Middle</option>
                              <option value="Rear">Rear</option>
                            </select>
                            <select
                              value={tl.position}
                              onChange={(e) => setTirelocs((prev) => prev.map((t, i) => i === idx ? { ...t, position: e.target.value } : t))}
                              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading"
                            >
                              <option value="Inner">Inner</option>
                              <option value="Outer">Outer</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => setTirelocs((prev) => prev.filter((_, i) => i !== idx))}
                              className="p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Remove tire"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="col-span-2">
                <label className="block text-xs font-medium text-muted mb-1">
                  Comments
                </label>
                <textarea
                  value={formData.repaircomments}
                  onChange={(e) => handleFieldChange('repaircomments', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                  placeholder="Add comments..."
                />
              </div>
            </div>
          </div>

          {/* ── Dispatch Information (edit mode only) ── */}
          {isEditMode && (
            <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-heading mb-4">
                Dispatch Information
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {/* Vendor */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted mb-1">
                    Vendor
                  </label>
                  <div className={isStaleDispatch('vendorrecno') ? 'ring-2 ring-amber-500 rounded-md' : ''}>
                    <TypeAhead
                      options={vendorOptions}
                      value={dispatchForm.vendorrecno ? String(dispatchForm.vendorrecno) : ''}
                      onChange={(val) => handleDispatchFieldChange('vendorrecno', val ? Number(val) : null)}
                      placeholder="Search vendors..."
                      portalContainer={portalContainer}
                      onCreateNew={(searchTerm) => {
                        setVendorCreateName(searchTerm);
                        setVendorCreateError(null);
                        setVendorCreateOpen(true);
                      }}
                    />
                  </div>
                </div>

                {/* Repair Email */}
                <div className="col-span-2">
                  <label htmlFor="repair-email" className="block text-xs font-medium text-muted mb-1">
                    Repair Email
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="repair-email"
                      type="text"
                      inputMode="email"
                      aria-describedby="repair-email-help"
                      value={dispatchForm.repairemail || ''}
                      onChange={(e) => handleDispatchFieldChange('repairemail', e.target.value)}
                      placeholder="vendor@example.com; dispatch@example.com"
                      className={`flex-1 px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 ${isStaleDispatch('repairemail') ? STALE_INPUT_CLASS : 'border-gray-300 dark:border-gray-600'}`}
                    />
                    <button
                      type="button"
                      onClick={handleSendVendorEmail}
                      disabled={!dispatchForm.repairemail?.trim() || !isValidEmailList(dispatchForm.repairemail || '', { semicolon: true }) || sendingVendorEmail}
                      title={!dispatchForm.repairemail?.trim() ? 'Enter a repair email first' : !isValidEmailList(dispatchForm.repairemail || '', { semicolon: true }) ? 'Enter valid repair email addresses separated by commas or semicolons' : 'Send repair order details to all listed addresses'}
                      className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {sendingVendorEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                  <p id="repair-email-help" className="mt-1 text-xs text-muted">
                    Separate multiple email addresses with commas or semicolons.
                  </p>
                  {vendorEmailStatus === 'sent' && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">Email sent successfully.</p>
                  )}
                  {vendorEmailStatus === 'error' && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">Failed to send email. Please try again.</p>
                  )}
                </div>

                {/* Estimated Dispatch Date/Time */}
                <div className="col-span-2">
                  <div className={isStaleDispatch('estimateddispatchdatetime') ? 'ring-2 ring-amber-500 rounded-md' : ''}>
                    <DateTimePicker
                      mode="datetime"
                      inputVariant="modal"
                      selectionType="single"
                      use24Hour
                      autoFlip
                      portalContainer={portalContainer}
                      label="Estimated Dispatch Date/Time"
                      labelType="custom"
                      value={dispatchForm.estimateddispatchdatetime ? new Date(dispatchForm.estimateddispatchdatetime) : null}
                      onChange={(date) => {
                        if (!date) { handleDispatchFieldChange('estimateddispatchdatetime', null); return; }
                        const d = date as Date;
                        const pad = (n: number) => String(n).padStart(2, '0');
                        handleDispatchFieldChange('estimateddispatchdatetime', `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                      }}
                    />
                  </div>
                </div>

                {/* Vendor Dispatched Time */}
                <div className="col-span-2">
                  <div className={isStaleDispatch('repairdispatchdatetime') ? 'ring-2 ring-amber-500 rounded-md' : ''}>
                    <DateTimePicker
                      mode="datetime"
                      inputVariant="modal"
                      selectionType="single"
                      use24Hour
                      autoFlip
                      portalContainer={portalContainer}
                      label="Vendor Dispatched Time"
                      labelType="custom"
                      value={dispatchForm.repairdispatchdatetime ? new Date(dispatchForm.repairdispatchdatetime) : null}
                      onChange={(date) => {
                        if (!date) { handleDispatchFieldChange('repairdispatchdatetime', null); return; }
                        const d = date as Date;
                        const pad = (n: number) => String(n).padStart(2, '0');
                        handleDispatchFieldChange('repairdispatchdatetime', `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                      }}
                    />
                  </div>
                </div>

                {/* Dispatched By */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted mb-1">
                    Dispatched By
                  </label>
                  <div className={isStaleDispatch('dispatchedby') ? 'ring-2 ring-amber-500 rounded-md' : ''}>
                    <TypeAhead
                      options={employeeOptions}
                      value={dispatchForm.dispatchedby || ''}
                      onChange={(val) => handleDispatchFieldChange('dispatchedby', val)}
                      placeholder="Search employees..."
                      portalContainer={portalContainer}
                    />
                  </div>
                </div>

                {/* ETA Minutes */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted mb-1">
                    ETA Minutes{dispatchForm.vendorrecno && dispatchForm.repairdispatchdatetime && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={dispatchForm.repaireta || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      handleDispatchFieldChange('repaireta', val);
                    }}
                    className={`w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 ${isStaleDispatch('repaireta') ? STALE_INPUT_CLASS : 'border-gray-300 dark:border-gray-600'}`}
                    placeholder="e.g. 90"
                  />
                </div>

                {/* ETA to Repair Location (auto-calculated, read-only) */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted mb-1">
                    ETA to Repair Location{dispatchForm.vendorrecno && dispatchForm.repairdispatchdatetime && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <input
                    type="datetime-local"
                    readOnly
                    value={dispatchForm.vendoretadatetime?.slice(0, 16) || ''}
                    className={`w-full px-3 py-2 text-sm border rounded-md bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white cursor-default ${isStaleDispatch('vendoretadatetime') ? STALE_INPUT_CLASS : 'border-gray-300 dark:border-gray-600'}`}
                  />
                </div>

                {/* Driver Comments */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-muted mb-1">
                    Driver Comments
                  </label>
                  <textarea
                    value={dispatchForm.drivercomments || ''}
                    onChange={(e) => handleDispatchFieldChange('drivercomments', e.target.value)}
                    rows={3}
                    className={`w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 ${isStaleDispatch('drivercomments') ? STALE_INPUT_CLASS : 'border-gray-300 dark:border-gray-600'}`}
                    placeholder="Notes for the driver..."
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Billing Information — only after the order is created. Kept inside the
            loaded container (so it waits for the order data) but as a sibling of
            the dimmed block, so it stays editable on Completed; locked on
            Closed/Cancelled. */}
        {isEditMode && orderRecno && (
        <div className="px-4 sm:px-6 pb-6">
          <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4${isBillingLocked ? ' pointer-events-none opacity-60 select-none' : ''}`}>
            <h3 className="text-sm font-semibold text-heading mb-4">
              Billing Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Estimated Cost */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Estimated Cost
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.estimatedcost ?? ''}
                    onChange={(e) => handleFieldChange('estimatedcost', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder="0.00"
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Invoice # */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Invoice #
                </label>
                <input
                  type="text"
                  value={formData.invoicenumber}
                  onChange={(e) => handleFieldChange('invoicenumber', e.target.value)}
                  placeholder="Invoice number"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Invoice Total */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Invoice Total
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.invoicetotal ?? ''}
                    onChange={(e) => handleFieldChange('invoicetotal', e.target.value === '' ? null : Number(e.target.value))}
                    placeholder="0.00"
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Billed To — type picker. Changing the type clears the entity
                  selection, since the options come from a different lookup. */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Billed To
                </label>
                <select
                  value={formData.billedtotype}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      billedtotype: nextType,
                      billedtorecno: null,
                      billedtoname: '',
                    }));
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— None —</option>
                  {BilledToTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Billed To — entity picker, sourced from the lookup matching
                  the selected type. Disabled until a type is chosen. */}
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  {formData.billedtotype || 'Billed To'} Name
                </label>
                <select
                  value={formData.billedtorecno ?? ''}
                  disabled={!formData.billedtotype}
                  onChange={(e) => {
                    const recno = e.target.value === '' ? null : Number(e.target.value);
                    const match = billedToOptions.find((o) => o.recno === recno);
                    setFormData((prev) => ({
                      ...prev,
                      billedtorecno: recno,
                      billedtoname: match?.name ?? '',
                    }));
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {formData.billedtotype ? `Select ${formData.billedtotype.toLowerCase()}…` : 'Select a type first'}
                  </option>
                  {billedToOptions.map((o) => (
                    <option key={o.recno} value={o.recno}>{o.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        )}
        </>
      ) : null}

      {/* Documents — always interactive regardless of order status */}
      {activeTab === 'information' && isEditMode && orderRecno ? (
        <div className="px-4 sm:px-6 pb-6">
          <RepairOrderDocuments orderRecno={orderRecno} isReadOnly={false} pronumber={formData.pronumber} />
        </div>
      ) : activeTab === 'information' && !isEditMode ? (
        <div className="px-4 sm:px-6 pb-6">
          <RepairOrderDocuments
            orderRecno={null}
            isReadOnly={false}
            pendingFiles={pendingFiles}
            onPendingFilesChange={setPendingFiles}
          />
        </div>
      ) : null}

      {/* Quick-create vendor modal */}
      {vendorCreateOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div className="absolute inset-0 bg-scrim/50" onClick={() => { setVendorCreateOpen(false); setVendorCreateName(''); setVendorCreateEmail(''); }} />
          <div
            ref={vendorCreatePanelRef}
            style={vendorCreatePanelStyle}
            className="relative bg-panel rounded-lg shadow-xl w-full max-w-sm mx-4"
          >
            <ModalHeader
              title="Add New Vendor"
              onClose={vendorCreateSaving ? undefined : () => { setVendorCreateOpen(false); setVendorCreateName(''); setVendorCreateEmail(''); }}
              dragHandleProps={vendorCreateDragHandleProps}
            />
            <div className="p-6">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-label mb-1">
                  Vendor Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={vendorCreateName}
                  onChange={(e) => setVendorCreateName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleVendorCreateNew(); }}
                  autoFocus
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-heading focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  disabled={vendorCreateSaving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-label mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={vendorCreateEmail}
                  onChange={(e) => setVendorCreateEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleVendorCreateNew(); }}
                  placeholder="vendor@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-heading focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  disabled={vendorCreateSaving}
                />
              </div>
              {vendorCreateError && (
                <p className="text-xs text-red-600 dark:text-red-400">{vendorCreateError}</p>
              )}
              <p className="text-xs text-muted">
                Additional details (address, phone) can be filled in from Data Management → Repair Vendors.
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => { setVendorCreateOpen(false); setVendorCreateName(''); setVendorCreateEmail(''); }}
                disabled={vendorCreateSaving}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVendorCreateNew}
                disabled={vendorCreateSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {vendorCreateSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Add Vendor'
                )}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Repair Order History Drawer */}
      <HistoryDrawer
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        endpoint={orderRecno ? `/repair-orders/${orderRecno}/history` : null}
        title={`RO #${orderRecno} History`}
        tableDisplayNames={{
          repairorder: 'Repair Order',
          repairrequest: 'Repair Request',
          repairdispatch: 'Dispatch',
        }}
      />
    </Drawer>
  );
}
